import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ResolvedPeriod } from '../PeriodPicker';
import { DrillDownRow, QualityApi } from './qualityApi';

const JIRA_BROWSE = 'https://hrsinternational.atlassian.net/browse/';
const PAGE_SIZE = 50;

interface DrillDownModalProps {
  by: 'component' | 'label' | 'bugType';
  value: string;
  resolvedPeriod: ResolvedPeriod | null;
  onClose: () => void;
}

export const DrillDownModal: React.FC<DrillDownModalProps> = ({
  by,
  value,
  resolvedPeriod,
  onClose,
}) => {
  const [rows, setRows] = useState<DrillDownRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setRows([]);
    setOffset(0);
    setHasMore(true);
  }, [by, value, resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await QualityApi.getDrillDown(
          { period: null, resolvedPeriod },
          { by, value, limit: PAGE_SIZE, offset }
        );
        if (!cancelled) {
          if (offset === 0) setRows(data);
          else setRows((prev) => [...prev, ...data]);
          setHasMore(data.length === PAGE_SIZE);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load issues');
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, by, value, resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="issue-comment-overlay issue-comment-overlay--fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label="Drill-down issues"
      onClick={onClose}
    >
      <div
        className="issue-comment-modal issue-comment-modal--fullscreen chart-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="issue-comment-modal__header">
          <div className="issue-comment-modal__title">
            <span>
              Issues in {by}: <strong>{value}</strong>
            </span>
          </div>
          <button
            type="button"
            className="issue-comment-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="issue-comment-modal__body-scroll">
          {error && <p className="quality-breakdown__empty">Failed to load: {error}</p>}
          {!error && rows.length === 0 && !loading && (
            <p className="quality-breakdown__empty">No issues found.</p>
          )}
          {rows.length > 0 && (
            <table className="estimation-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Summary</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Assignee</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <a
                        href={`${JIRA_BROWSE}${encodeURIComponent(r.key)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="task-link"
                      >
                        {r.key}
                      </a>
                    </td>
                    <td>{r.summary}</td>
                    <td>{r.status || '—'}</td>
                    <td>{r.type || '—'}</td>
                    <td>{r.assignee || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="issue-comment-modal__footer">
          <span className="issue-comment-modal__hint">
            {rows.length} loaded{hasMore ? ' — more available' : ''}
          </span>
          <div className="issue-comment-modal__actions">
            {hasMore && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setOffset(rows.length)}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Show more'}
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
